/**
 * Capa única de eventos de conversión sobre GA4 (gtag.js).
 * GA4 ya está cargado en index.html (G-PV0CVXE80G). No se añade GTM ni
 * ninguna segunda librería de analítica para evitar duplicación de hits.
 *
 * Si en el futuro se instala GTM, este helper debe cambiar a dataLayer.push
 * en un único punto, sin tocar los componentes.
 */

export type ConversionEvent =
  | 'primary_cta_click'
  | 'secondary_cta_click'
  | 'form_start'
  | 'form_submit'
  | 'phone_click'
  | 'email_click'
  | 'calendar_click'
  | 'language_switch'
  | 'case_study_view'
  | 'resource_download'
  | 'tool_start'
  | 'tool_complete'
  | 'article_view'
  | 'resource_cta_click'
  | 'service_from_content';

type EventParams = Record<string, string | number | boolean | undefined>;

export function trackEvent(event: ConversionEvent, params: EventParams = {}) {
  if (typeof window === 'undefined') return;
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== 'function') return;

  gtag('event', event, {
    page_path: window.location.pathname,
    language: window.location.pathname.startsWith('/en') ? 'en' : 'es',
    ...params,
  });
}

/** Handler listo para onClick de CTAs primarios. */
export const trackPrimaryCta = (label: string, location: string) =>
  trackEvent('primary_cta_click', { cta_label: label, cta_location: location });

export const trackSecondaryCta = (label: string, location: string) =>
  trackEvent('secondary_cta_click', { cta_label: label, cta_location: location });

/** Lectura de una pieza de la Search Library. */
export const trackArticleView = (articleId: string, cluster: string, format: string) =>
  trackEvent('article_view', { article_id: articleId, cluster, format });

/** Clic en el CTA contextual de un artículo. */
export const trackResourceCta = (articleId: string, destination: string) =>
  trackEvent('resource_cta_click', { article_id: articleId, destination });

/** Salto desde contenido editorial hacia una página de servicio. */
export const trackServiceFromContent = (articleId: string, destination: string) =>
  trackEvent('service_from_content', { article_id: articleId, destination });
